/**
 * Location Selector - Blinkit Style
 * Handles geolocation and address selection
 */

class LocationSelector {
    constructor() {
        this.locationBar = document.querySelector('.blinkit-location');
        this.addressDisplay = document.querySelector('.blinkit-location-address');
        this.modal = null;
        this.defaultLocation = {
            city: 'Chennai',
            state: 'Tamil Nadu',
            lat: 13.0827,
            lng: 80.2707
        };
        
        this.init();
    }
    
    init() {
        if (!this.locationBar) return;
        
        // Make location clickable
        this.locationBar.style.cursor = 'pointer';
        this.locationBar.addEventListener('click', () => this.openLocationModal());
        
        // Load saved location
        this.loadSavedLocation();
    }
    
    loadSavedLocation() {
        const saved = localStorage.getItem('sk_delivery_location');
        if (saved) {
            try {
                const location = JSON.parse(saved);
                this.updateDisplay(location.city, location.state);
            } catch (e) {
                console.log('No saved location');
            }
        }
    }
    
    saveLocation(city, state, lat, lng) {
        const location = { city, state, lat, lng };
        localStorage.setItem('sk_delivery_location', JSON.stringify(location));
        this.updateDisplay(city, state);
    }
    
    updateDisplay(city, state) {
        if (this.addressDisplay) {
            const svg = this.addressDisplay.querySelector('svg');
            this.addressDisplay.innerHTML = `${city}, ${state} `;
            if (svg) this.addressDisplay.appendChild(svg);
        }
    }
    
    openLocationModal() {
        // Create modal
        this.modal = document.createElement('div');
        this.modal.className = 'location-modal';
        this.modal.innerHTML = `
            <div class="location-modal-overlay"></div>
            <div class="location-modal-content">
                <div class="location-modal-header">
                    <h3>📍 Select Delivery Location</h3>
                    <button class="location-modal-close">&times;</button>
                </div>
                <div class="location-modal-body">
                    <button class="location-current-btn">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="12" cy="12" r="10"/>
                            <circle cx="12" cy="12" r="3"/>
                            <path d="M12 2v2M12 20v2M2 12h2M20 12h2"/>
                        </svg>
                        Use My Current Location
                    </button>
                    <div class="location-divider"><span>or</span></div>
                    <div class="location-search">
                        <input type="text" placeholder="Enter city or pincode..." class="location-search-input" id="locationSearchInput">
                        <button class="location-search-btn">Search</button>
                    </div>
                    <div class="location-results" id="locationResults"></div>
                    <div class="location-popular">
                        <p class="location-popular-title">Popular Cities</p>
                        <div class="location-popular-list">
                            <button data-city="Chennai" data-state="Tamil Nadu">Chennai</button>
                            <button data-city="Bangalore" data-state="Karnataka">Bangalore</button>
                            <button data-city="Hyderabad" data-state="Telangana">Hyderabad</button>
                            <button data-city="Mumbai" data-state="Maharashtra">Mumbai</button>
                            <button data-city="Delhi" data-state="Delhi">Delhi</button>
                            <button data-city="Coimbatore" data-state="Tamil Nadu">Coimbatore</button>
                        </div>
                    </div>
                </div>
                <div class="location-modal-footer">
                    <p class="location-note">🚚 We deliver across India!</p>
                </div>
            </div>
        `;
        
        document.body.appendChild(this.modal);
        document.body.style.overflow = 'hidden';
        
        // Add styles
        this.addModalStyles();
        
        // Event listeners
        this.modal.querySelector('.location-modal-overlay').addEventListener('click', () => this.closeModal());
        this.modal.querySelector('.location-modal-close').addEventListener('click', () => this.closeModal());
        this.modal.querySelector('.location-current-btn').addEventListener('click', () => this.getCurrentLocation());
        this.modal.querySelector('.location-search-btn').addEventListener('click', () => this.searchLocation());
        this.modal.querySelector('#locationSearchInput').addEventListener('keypress', (e) => {
            if (e.key === 'Enter') this.searchLocation();
        });
        
        // Popular cities
        this.modal.querySelectorAll('.location-popular-list button').forEach(btn => {
            btn.addEventListener('click', () => {
                const city = btn.dataset.city;
                const state = btn.dataset.state;
                this.saveLocation(city, state, 0, 0);
                this.closeModal();
            });
        });
    }
    
    closeModal() {
        if (this.modal) {
            this.modal.remove();
            this.modal = null;
            document.body.style.overflow = '';
        }
    }
    
    getCurrentLocation() {
        const btn = this.modal.querySelector('.location-current-btn');
        btn.innerHTML = '<span class="spinner"></span> Getting location...';
        btn.disabled = true;
        
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => this.reverseGeocode(position.coords.latitude, position.coords.longitude),
                (error) => {
                    btn.innerHTML = '❌ Location access denied';
                    setTimeout(() => {
                        btn.innerHTML = `
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <circle cx="12" cy="12" r="10"/>
                                <circle cx="12" cy="12" r="3"/>
                                <path d="M12 2v2M12 20v2M2 12h2M20 12h2"/>
                            </svg>
                            Use My Current Location
                        `;
                        btn.disabled = false;
                    }, 2000);
                },
                { enableHighAccuracy: true, timeout: 10000 }
            );
        } else {
            btn.innerHTML = '❌ Geolocation not supported';
        }
    }
    
    async reverseGeocode(lat, lng) {
        try {
            // Using OpenStreetMap Nominatim (free, no API key required)
            const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`,
                { headers: { 'Accept-Language': 'en' } }
            );
            const data = await response.json();
            
            const address = data.address;
            const city = address.city || address.town || address.village || address.county || 'Unknown';
            const state = address.state || 'India';
            
            this.saveLocation(city, state, lat, lng);
            this.closeModal();
        } catch (error) {
            console.error('Reverse geocoding failed:', error);
            // Fallback
            this.saveLocation('Your Location', 'Detected', lat, lng);
            this.closeModal();
        }
    }
    
    async searchLocation() {
        const input = this.modal.querySelector('#locationSearchInput');
        const query = input.value.trim();
        const resultsDiv = this.modal.querySelector('#locationResults');
        
        if (!query) return;
        
        resultsDiv.innerHTML = '<p class="searching">Searching...</p>';
        
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)},India&addressdetails=1&limit=5`,
                { headers: { 'Accept-Language': 'en' } }
            );
            const data = await response.json();
            
            if (data.length === 0) {
                resultsDiv.innerHTML = '<p class="no-results">No results found. Try a different search.</p>';
                return;
            }
            
            resultsDiv.innerHTML = '';
            data.forEach(place => {
                const address = place.address;
                const city = address.city || address.town || address.village || address.county || query;
                const state = address.state || 'India';
                
                const btn = document.createElement('button');
                btn.className = 'location-result-item';
                btn.innerHTML = `
                    <span class="result-icon">📍</span>
                    <span class="result-text">
                        <strong>${city}</strong>
                        <small>${state}</small>
                    </span>
                `;
                btn.addEventListener('click', () => {
                    this.saveLocation(city, state, place.lat, place.lon);
                    this.closeModal();
                });
                resultsDiv.appendChild(btn);
            });
        } catch (error) {
            resultsDiv.innerHTML = '<p class="error">Search failed. Please try again.</p>';
        }
    }
    
    addModalStyles() {
        if (document.getElementById('location-modal-styles')) return;
        
        const styles = document.createElement('style');
        styles.id = 'location-modal-styles';
        styles.textContent = `
            .location-modal {
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                z-index: 9999;
                display: flex;
                align-items: center;
                justify-content: center;
            }
            .location-modal-overlay {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background: rgba(0,0,0,0.5);
                backdrop-filter: blur(4px);
            }
            .location-modal-content {
                position: relative;
                background: #fff;
                border-radius: 16px;
                width: 90%;
                max-width: 420px;
                max-height: 90vh;
                overflow-y: auto;
                box-shadow: 0 20px 60px rgba(0,0,0,0.3);
                animation: modalSlideIn 0.3s ease;
            }
            @keyframes modalSlideIn {
                from { transform: translateY(-20px); opacity: 0; }
                to { transform: translateY(0); opacity: 1; }
            }
            .location-modal-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 1.25rem 1.5rem;
                border-bottom: 1px solid #eee;
            }
            .location-modal-header h3 {
                margin: 0;
                font-size: 1.1rem;
                color: #333;
            }
            .location-modal-close {
                background: none;
                border: none;
                font-size: 1.5rem;
                cursor: pointer;
                color: #666;
                padding: 0;
                line-height: 1;
            }
            .location-modal-body {
                padding: 1.5rem;
            }
            .location-current-btn {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 0.5rem;
                padding: 1rem;
                background: linear-gradient(135deg, #2E7D32, #4CAF50);
                color: #fff;
                border: none;
                border-radius: 10px;
                font-size: 1rem;
                font-weight: 600;
                cursor: pointer;
                transition: transform 0.2s, box-shadow 0.2s;
            }
            .location-current-btn:hover:not(:disabled) {
                transform: translateY(-2px);
                box-shadow: 0 4px 15px rgba(46,125,50,0.4);
            }
            .location-current-btn:disabled {
                opacity: 0.7;
                cursor: not-allowed;
            }
            .spinner {
                width: 18px;
                height: 18px;
                border: 2px solid rgba(255,255,255,0.3);
                border-top-color: #fff;
                border-radius: 50%;
                animation: spin 0.8s linear infinite;
            }
            @keyframes spin {
                to { transform: rotate(360deg); }
            }
            .location-divider {
                display: flex;
                align-items: center;
                margin: 1.25rem 0;
                color: #999;
                font-size: 0.85rem;
            }
            .location-divider::before,
            .location-divider::after {
                content: '';
                flex: 1;
                height: 1px;
                background: #eee;
            }
            .location-divider span {
                padding: 0 1rem;
            }
            .location-search {
                display: flex;
                gap: 0.5rem;
            }
            .location-search-input {
                flex: 1;
                padding: 0.875rem 1rem;
                border: 2px solid #e0e0e0;
                border-radius: 10px;
                font-size: 0.95rem;
                transition: border-color 0.2s;
            }
            .location-search-input:focus {
                outline: none;
                border-color: #2E7D32;
            }
            .location-search-btn {
                padding: 0.875rem 1.25rem;
                background: #333;
                color: #fff;
                border: none;
                border-radius: 10px;
                font-weight: 600;
                cursor: pointer;
                transition: background 0.2s;
            }
            .location-search-btn:hover {
                background: #1a1a1a;
            }
            .location-results {
                margin-top: 1rem;
            }
            .location-result-item {
                width: 100%;
                display: flex;
                align-items: center;
                gap: 0.75rem;
                padding: 0.875rem 1rem;
                background: #f8f8f8;
                border: none;
                border-radius: 8px;
                margin-bottom: 0.5rem;
                cursor: pointer;
                text-align: left;
                transition: background 0.2s;
            }
            .location-result-item:hover {
                background: #e8f5e9;
            }
            .result-icon {
                font-size: 1.25rem;
            }
            .result-text {
                display: flex;
                flex-direction: column;
            }
            .result-text strong {
                color: #333;
            }
            .result-text small {
                color: #666;
                font-size: 0.8rem;
            }
            .searching, .no-results, .error {
                text-align: center;
                padding: 1rem;
                color: #666;
            }
            .location-popular {
                margin-top: 1.5rem;
            }
            .location-popular-title {
                font-size: 0.85rem;
                color: #666;
                margin-bottom: 0.75rem;
            }
            .location-popular-list {
                display: flex;
                flex-wrap: wrap;
                gap: 0.5rem;
            }
            .location-popular-list button {
                padding: 0.5rem 1rem;
                background: #f0f0f0;
                border: 1px solid #e0e0e0;
                border-radius: 20px;
                font-size: 0.85rem;
                cursor: pointer;
                transition: all 0.2s;
            }
            .location-popular-list button:hover {
                background: #e8f5e9;
                border-color: #2E7D32;
                color: #2E7D32;
            }
            .location-modal-footer {
                padding: 1rem 1.5rem;
                border-top: 1px solid #eee;
                background: #f9f9f9;
                border-radius: 0 0 16px 16px;
            }
            .location-note {
                margin: 0;
                text-align: center;
                color: #666;
                font-size: 0.85rem;
            }
        `;
        document.head.appendChild(styles);
    }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    new LocationSelector();
});

